import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Clock, CalendarDays } from "lucide-react";
import { fetchBookingBySession } from "@/lib/api";

const money = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

export default function BookingConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("loading"); // loading | paid | pending | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const b = await fetchBookingBySession(sessionId);
        if (!active) return;
        setBooking(b);
        if (b.deposit_status === "paid") { setState("paid"); return; }
        if (tries.current < 8) { tries.current += 1; setTimeout(poll, 2500); }
        else setState("pending");
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  if (state === "loading") {
    return <div className="flex min-h-[70vh] items-center justify-center" data-testid="booking-loading"><Loader2 className="h-8 w-8 animate-spin text-steel-brass" /></div>;
  }

  return (
    <div className="mx-auto max-w-xl px-6 py-28 text-center" data-testid="booking-confirmed-page">
      {state === "paid" ? (
        <>
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
          <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Consultation confirmed</h1>
          {booking && (
            <div className="mt-6 border border-steel-border bg-steel-surface p-6 text-left">
              <p className="font-display text-lg text-steel-text">{booking.type_name}</p>
              <p className="mt-2 flex items-center gap-2 text-sm text-steel-secondary"><CalendarDays className="h-4 w-4 text-steel-brass" />
                {new Date(booking.start).toLocaleString("en-US", { timeZone: booking.timezone, weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
              <p className="mt-1 flex items-center gap-2 text-sm text-steel-muted"><Clock className="h-4 w-4" /> {booking.duration_minutes} minutes</p>
              {booking.deposit_amount > 0 && (
                <p className="mt-4 border-t border-steel-border pt-4 text-sm text-steel-secondary">
                  {money(booking.deposit_amount)} deposit received — now sitting as <strong className="text-steel-brass">account credit</strong> toward your next Steele Industries bill.
                </p>
              )}
            </div>
          )}
          <p className="mt-6 text-sm text-steel-secondary">A confirmation email is on its way. Create a portal account with this same email to track your credit and invoices.</p>
          <Link to="/portal?signup=1" data-testid="booking-portal-link" className="mt-6 inline-flex bg-steel-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover">Set up my portal account</Link>
        </>
      ) : state === "pending" ? (
        <>
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-steel-brass" />
          <h1 className="mt-6 font-display text-2xl font-medium text-steel-text">Finishing up…</h1>
          <p className="mt-3 text-sm text-steel-secondary">Your payment is processing. We'll email your confirmation the moment it clears.</p>
        </>
      ) : (
        <>
          <h1 className="font-display text-2xl font-medium text-steel-text">Booking not found</h1>
          <p className="mt-3 text-sm text-steel-secondary">We couldn't locate this booking. Please <Link to="/book" className="text-steel-brass underline">try booking again</Link>.</p>
        </>
      )}
    </div>
  );
}
